import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { CustomerResponse, PspResponse } from '../../../../api';
import { BaseAvatarSize } from '../../../shared/components/avatar/base-avatar.component';
import { BaseRolesService } from '../../../core/services/base-roles.service';
import { SharedState } from '../../../_store/reducers/base-shared.reducer';
import {
  baseAdministrationActions,
  baseAdministrationSelectors,
  baseSharedSelectors,
  baseAuthActions,
} from '../../../_store';
import { TabWithPermissionsInterface } from '../../../shared/interfaces/base-tab-with-permissions.interface';
import { PORTAL_SETTINGS } from '../../../core/token/base-config.token';
import { PortalSettings } from '../../../core/types/base-portal-settings.type';
import { ENVIRONMENTER } from '../../../core/token/base-environmenter.token';
import { BaseApplication } from '../../../shared/enums/base-application.enum';

@Component({
  selector: 'base-user-nav',
  templateUrl: './base-user-nav.component.html',
})
export class BaseUserNavComponent implements OnInit, OnDestroy {
  @Input() userMenu: TabWithPermissionsInterface[] = [];
  @Output() openUserMenuSidebar: EventEmitter<void> = new EventEmitter<void>();

  isOpen = false;
  userName = '';
  userAvatar: string | undefined = '';
  avatarSize = BaseAvatarSize.intermediate;
  userMenuItems: TabWithPermissionsInterface[] = [];
  position: string | undefined;
  companyData: CustomerResponse | PspResponse | undefined;
  signDigital: boolean | undefined;
  readonly bedarfsradarUrl = 'https://stazzle.de/psp-bedarfsradar-pflege/';
  readonly isGrowth: boolean;

  private destroy$ = new Subject();

  constructor(
    private readonly roleService: BaseRolesService,
    private readonly store: Store<SharedState>,
    @Inject(PORTAL_SETTINGS) private portalConfig: PortalSettings,
    @Inject(ENVIRONMENTER) environment: any
  ) {
    this.isGrowth = environment?.application?.app === BaseApplication.growth;
  }

  get portalSettings(): PortalSettings {
    return this.portalConfig;
  }

  ngOnInit(): void {
    this.userMenuItems = this.userMenu?.filter((item) => {
      return this.roleService.isPsp()
        ? !item.permissionsAllowed.length ||
            item.permissionsAllowed.some((permission) => this.roleService.hasPspFeaturePermission(permission))
        : !item.rolesAllowed.length || item.rolesAllowed.some((role) => this.roleService.hasRole(role));
    });

    this.store
      .select(baseSharedSelectors.getProfile)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((state) => state.data)
      )
      .subscribe((value) => {
        if (value) {
          this.userName = value.firstName + ' ' + value.lastName;
          this.userAvatar = value.avatar?.data;
          this.position = value.position;
        }
      });

    if (this.roleService.isPsp()) {
      this.store
        .select(baseAdministrationSelectors.getPspData)
        .pipe(
          takeUntil(this.destroy$),
          filter((state) => !!state.ok),
          map((state) => state.data)
        )
        .subscribe((value) => {
          if (value) {
            this.companyData = value;
            this.signDigital = value.signDigital;
          } else {
            this.store.dispatch(baseAdministrationActions.loadPspData());
          }
        });
    } else {
      this.store
        .select(baseAdministrationSelectors.getCustomerData)
        .pipe(
          takeUntil(this.destroy$),
          filter((state) => !!state.ok),
          map((state) => state.data)
        )
        .subscribe((value) => {
          if (value) {
            this.companyData = value;
            this.signDigital = value.signDigital;
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  close(): void {
    this.isOpen = false;
  }

  logout(event: MouseEvent) {
    event.preventDefault();

    this.store.dispatch(baseAuthActions.logout());
  }
}
